import React, { useState, useEffect } from 'react';
import { CheckCircle2, Circle, Plus, Shield, Clock, AlertTriangle, ArrowLeft, Loader2 } from 'lucide-react';
import { api } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { replicaService } from '../lib/services';

// Types
interface Task {
    id: string;
    title: string;
    status: 'PENDING' | 'DONE';
    priority: 'LOW' | 'MEDIUM' | 'HIGH';
    createdAt: string;
}

interface Escalation {
    escalationId: string;
    debugToken: string;
    status?: string;
}

interface Props {
    onBack?: () => void;
}

export const TaskManagementPage: React.FC<Props> = ({ onBack }) => {
    const { user } = useAuth();
    const [backendReplicaId, setBackendReplicaId] = useState<string | null>(null);
    const [tasks, setTasks] = useState<Task[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    // Create task form
    const [newTaskTitle, setNewTaskTitle] = useState('');
    const [newTaskPriority, setNewTaskPriority] = useState<'LOW' | 'MEDIUM' | 'HIGH'>('MEDIUM');
    const [creating, setCreating] = useState(false);

    // Escalation state
    const [pendingEscalation, setPendingEscalation] = useState<Escalation | null>(null);
    const [approving, setApproving] = useState(false);

    // Fetch the backend replica UUID on mount
    useEffect(() => {
        if (!user?.uid) return;
        replicaService.getUserReplicas(user.uid).then(res => {
            if (res.data?.replicas?.length) {
                setBackendReplicaId(res.data.replicas[0].id);
            } else {
                setLoading(false);
                setError('No replica found. Please create a replica first.');
            }
        }).catch(() => {
            setLoading(false);
            setError('Failed to fetch replica info from backend.');
        });
    }, [user?.uid]);

    // Fetch tasks once we have the backend replica ID
    useEffect(() => {
        if (backendReplicaId) fetchTasks();
    }, [backendReplicaId]);

    const fetchTasks = async () => {
        if (!backendReplicaId) {
            setLoading(false);
            setError('No replica found. Please create a replica first.');
            return;
        }
        setLoading(true);
        setError(null);

        try {
            const { data, error: apiError } = await api.post<any>('/mcp/execute', {
                replicaId: backendReplicaId,
                toolName: 'tasks.read',
                input: {}
            });

            if (apiError) {
                setError(apiError);
            } else if (data?.ok && data?.result?.tasks) {
                setTasks(data.result.tasks);
            } else {
                setError(data?.error || 'Failed to fetch tasks');
            }
        } catch (err) {
            setError('Network error: Could not connect to backend');
        } finally {
            setLoading(false);
        }
    };

    const handleCreateTask = async () => {
        if (!newTaskTitle.trim()) return;

        setCreating(true);
        setError(null);

        try {
            const { data, error: apiError } = await api.post<any>('/mcp/execute', {
                replicaId: backendReplicaId!,
                toolName: 'tasks.create',
                input: {
                    title: newTaskTitle.trim(),
                    priority: newTaskPriority
                }
            });

            if (apiError) {
                // Check if the response was a 202 (escalation) - api client treats non-2xx as errors
                // but the data may still contain the escalation info
                setError(apiError);
            } else if (data?.ok) {
                // Task created successfully
                setNewTaskTitle('');
                fetchTasks(); // Refresh list
            } else if (data?.requiresApproval) {
                // Escalation created - needs guardian approval
                setPendingEscalation({
                    escalationId: data.escalationId,
                    debugToken: data.debugToken,
                    status: 'PENDING'
                });
            } else {
                setError(data?.error || 'Failed to create task');
            }
        } catch (err) {
            setError('Network error: Could not connect to backend');
        } finally {
            setCreating(false);
        }
    };

    const handleApproveEscalation = async () => {
        if (!pendingEscalation) return;

        setApproving(true);
        setError(null);

        try {
            const { data, error: apiError } = await api.post<any>('/mcp/execute', {
                replicaId: backendReplicaId!,
                toolName: 'tasks.create',
                input: {
                    title: newTaskTitle.trim(),
                    priority: newTaskPriority
                },
                escalationId: pendingEscalation.escalationId,
                approvalToken: pendingEscalation.debugToken
            });

            if (apiError) {
                setError(apiError);
            } else if (data?.ok) {
                // Task created after approval
                setPendingEscalation(null);
                setNewTaskTitle('');
                fetchTasks(); // Refresh list
            } else {
                setError(data?.error || 'Failed to approve task');
            }
        } catch (err) {
            setError('Network error: Could not connect to backend');
        } finally {
            setApproving(false);
        }
    };

    const handleCancelEscalation = () => {
        setPendingEscalation(null);
    };

    const getPriorityColor = (priority: string) => {
        switch (priority) {
            case 'HIGH': return 'text-red-500 bg-red-500/10';
            case 'MEDIUM': return 'text-amber-500 bg-amber-500/10';
            case 'LOW': return 'text-green-500 bg-green-500/10';
            default: return 'text-gray-500 bg-gray-500/10';
        }
    };

    return (
        <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 text-white p-6">
            {/* Header */}
            <div className="max-w-3xl mx-auto">
                <div className="flex items-center gap-4 mb-8">
                    {onBack && (
                        <button
                            onClick={onBack}
                            className="p-2 rounded-lg hover:bg-white/5 transition-colors"
                        >
                            <ArrowLeft className="w-5 h-5" />
                        </button>
                    )}
                    <div>
                        <h1 className="text-2xl font-semibold">Task Management</h1>
                        <p className="text-slate-400 text-sm">MCP Gateway Local Execution Proof</p>
                    </div>
                </div>

                {/* Error Banner */}
                {error && (
                    <div className="mb-6 p-4 rounded-xl bg-red-500/10 border border-red-500/20 flex items-start gap-3">
                        <AlertTriangle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
                        <div>
                            <p className="text-red-400 font-medium">Error</p>
                            <p className="text-red-300/80 text-sm">{error}</p>
                        </div>
                    </div>
                )}

                {/* Escalation Approval Panel */}
                {pendingEscalation && (
                    <div className="mb-6 p-6 rounded-2xl bg-amber-500/10 border border-amber-500/30">
                        <div className="flex items-start gap-4">
                            <div className="p-3 rounded-xl bg-amber-500/20">
                                <Shield className="w-6 h-6 text-amber-400" />
                            </div>
                            <div className="flex-1">
                                <h3 className="text-lg font-semibold text-amber-300">Guardian Approval Required</h3>
                                <p className="text-amber-200/80 text-sm mt-1">
                                    This write operation requires human approval before execution.
                                </p>

                                <div className="mt-4 p-4 rounded-xl bg-black/20">
                                    <div className="text-sm space-y-2">
                                        <div className="flex justify-between">
                                            <span className="text-slate-400">Operation</span>
                                            <span className="text-amber-300 font-mono">tasks.create</span>
                                        </div>
                                        <div className="flex justify-between">
                                            <span className="text-slate-400">Task</span>
                                            <span className="text-white">{newTaskTitle}</span>
                                        </div>
                                        <div className="flex justify-between">
                                            <span className="text-slate-400">Priority</span>
                                            <span className={`px-2 py-0.5 rounded text-xs ${getPriorityColor(newTaskPriority)}`}>
                                                {newTaskPriority}
                                            </span>
                                        </div>
                                        <div className="flex justify-between">
                                            <span className="text-slate-400">Escalation ID</span>
                                            <span className="text-slate-500 font-mono text-xs">{pendingEscalation.escalationId.slice(0, 8)}...</span>
                                        </div>
                                    </div>
                                </div>

                                <div className="mt-4 flex gap-3">
                                    <button
                                        onClick={handleApproveEscalation}
                                        disabled={approving}
                                        className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl bg-green-500/20 text-green-300 hover:bg-green-500/30 transition-colors disabled:opacity-50"
                                    >
                                        {approving ? (
                                            <Loader2 className="w-4 h-4 animate-spin" />
                                        ) : (
                                            <CheckCircle2 className="w-4 h-4" />
                                        )}
                                        Approve & Execute
                                    </button>
                                    <button
                                        onClick={handleCancelEscalation}
                                        disabled={approving}
                                        className="px-4 py-2.5 rounded-xl bg-slate-500/20 text-slate-300 hover:bg-slate-500/30 transition-colors disabled:opacity-50"
                                    >
                                        Cancel
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {/* Create Task Form */}
                <div className="mb-8 p-6 rounded-2xl bg-white/5 border border-white/10">
                    <h2 className="text-lg font-medium mb-4 flex items-center gap-2">
                        <Plus className="w-5 h-5 text-hig-accent" />
                        Create New Task
                    </h2>

                    <div className="space-y-4">
                        <div>
                            <input
                                type="text"
                                value={newTaskTitle}
                                onChange={(e) => setNewTaskTitle(e.target.value)}
                                placeholder="Task title..."
                                className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 text-white placeholder-slate-500 focus:outline-none focus:border-hig-accent/50 transition-colors"
                                disabled={!!pendingEscalation}
                            />
                        </div>

                        <div className="flex items-center gap-4">
                            <label className="text-sm text-slate-400">Priority:</label>
                            <div className="flex gap-2">
                                {(['LOW', 'MEDIUM', 'HIGH'] as const).map((p) => (
                                    <button
                                        key={p}
                                        onClick={() => setNewTaskPriority(p)}
                                        className={`px-3 py-1.5 rounded-lg text-sm transition-all ${newTaskPriority === p
                                            ? getPriorityColor(p) + ' border border-current'
                                            : 'bg-white/5 text-slate-400 hover:bg-white/10'
                                            }`}
                                        disabled={!!pendingEscalation}
                                    >
                                        {p}
                                    </button>
                                ))}
                            </div>

                            <button
                                onClick={handleCreateTask}
                                disabled={!newTaskTitle.trim() || creating || !!pendingEscalation}
                                className="ml-auto flex items-center gap-2 px-5 py-2.5 rounded-xl bg-hig-accent text-[#0F172A] font-bold hover:bg-hig-accent transition-colors disabled:opacity-50 disabled:hover:bg-hig-accent"
                            >
                                {creating ? (
                                    <Loader2 className="w-4 h-4 animate-spin" />
                                ) : (
                                    <Plus className="w-4 h-4" />
                                )}
                                Create Task
                            </button>
                        </div>
                    </div>

                    <div className="mt-4 flex items-center gap-2 text-xs text-slate-500">
                        <Clock className="w-3.5 h-3.5" />
                        <span>Write operations require Guardian approval (Decision Guardian™)</span>
                    </div>
                </div>

                {/* Task List */}
                <div className="space-y-3">
                    <h2 className="text-lg font-medium text-slate-300 mb-4">Your Tasks</h2>

                    {loading ? (
                        <div className="flex items-center justify-center py-12">
                            <Loader2 className="w-6 h-6 animate-spin text-hig-accent" />
                        </div>
                    ) : tasks.length === 0 ? (
                        <div className="text-center py-12 text-slate-500">
                            <Circle className="w-12 h-12 mx-auto mb-3 opacity-30" />
                            <p>No tasks yet. Create one above!</p>
                        </div>
                    ) : (
                        tasks.map((task) => (
                            <div
                                key={task.id}
                                className="p-4 rounded-xl bg-white/5 border border-white/10 flex items-center gap-4 hover:bg-white/[0.07] transition-colors"
                            >
                                {task.status === 'DONE' ? (
                                    <CheckCircle2 className="w-5 h-5 text-green-400" />
                                ) : (
                                    <Circle className="w-5 h-5 text-slate-500" />
                                )}

                                <div className="flex-1">
                                    <p className={task.status === 'DONE' ? 'line-through text-slate-500' : 'text-white'}>
                                        {task.title}
                                    </p>
                                    <p className="text-xs text-slate-500 mt-0.5">
                                        {new Date(task.createdAt).toLocaleDateString()}
                                    </p>
                                </div>

                                <span className={`px-2.5 py-1 rounded-full text-xs ${getPriorityColor(task.priority)}`}>
                                    {task.priority}
                                </span>
                            </div>
                        ))
                    )}
                </div>
            </div>
        </div>
    );
};

export default TaskManagementPage;
